// Product Table Component - Dynamic table that handles any data structure

import React, { useMemo, useState } from 'react';
import { Table2, FileSpreadsheet, Loader2 } from 'lucide-react';
import { COLORS } from '../../constants/theme';
import URLCell from './URLCell';
import { isValidUrl, extractUrl } from '../../utils/urlUtils';
import api from '../../utils/api';

const ProductTable = ({ data, apiData }) => {
  const [exporting, setExporting] = useState({ excel: false });

  // Safely get column names from first row
  const columns = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') {
      return [];
    }
    
    return Object.keys(firstRow);
  }, [data]);

  // Check if 'id' column exists and should be displayed starting from 1
  const hasIdColumn = useMemo(() => {
    return columns.some(col => col.toLowerCase() === 'id');
  }, [columns]);

  // Check if a value is a URL
  const isUrlValue = (value, columnName) => {
    // Check if column name suggests it's a URL
    const urlColumnNames = ['url', 'link', 'href', 'uri', 'file', 'attachment', 'document'];
    const isUrlColumn = urlColumnNames.some(name => 
      columnName.toLowerCase().includes(name)
    );
    
    if (isUrlColumn && isValidUrl(value)) {
      return true;
    }
    
    // Check if value itself is a URL
    if (isValidUrl(value)) {
      return true;
    }
    
    // Check if value contains a URL
    if (typeof value === 'string' && extractUrl(value)) {
      return true;
    }
    
    return false;
  };

  // Format cell value safely
  const formatCellValue = (value, columnName = '') => {
    // Handle null/undefined
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    
    // Check if it's a URL - render as URL component
    if (isUrlValue(value, columnName)) {
      const url = isValidUrl(value) ? value : extractUrl(value);
      return <URLCell url={url} displayText={typeof value === 'string' && !isValidUrl(value) ? value : null} />;
    }
    
    // If it's a number, format it
    if (typeof value === 'number') {
      try {
        return value.toLocaleString();
      } catch {
        return String(value);
      }
    }
    
    // If it's a boolean, convert to string
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    // If it's a date string, try to format it
    if (typeof value === 'string') {
      // Check for date patterns
      if (value.match(/^\d{4}-\d{2}-\d{2}/) || value.match(/^\d{2}-\w{3}-\d{4}/)) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString();
          }
        } catch {
          // Fall through to return value as-is
        }
      }
      
      // Truncate if too long
      return value.length > 100 ? value.substring(0, 100) + '...' : value;
    }
    
    // For objects/arrays, stringify
    if (typeof value === 'object') {
      try {
        const str = JSON.stringify(value);
        return str.length > 100 ? str.substring(0, 100) + '...' : str;
      } catch {
        return '[Object]';
      }
    }
    
    // Return as string
    return String(value);
  };

  // Check if value looks like a number
  const isNumericColumn = (columnName, sampleValue) => {
    if (sampleValue === null || sampleValue === undefined) return false;
    if (typeof sampleValue === 'number') return true;
    if (typeof sampleValue === 'string') {
      const num = Number(sampleValue);
      return !isNaN(num) && isFinite(num) && sampleValue.trim() !== '';
    }
    return false;
  };

  // Determine column alignment
  const getColumnAlignment = (columnName, value) => {
    const lowerColName = columnName.toLowerCase();
    
    // ID column should be right-aligned
    if (lowerColName === 'id') {
      return 'text-right';
    }
    
    // Numeric columns should be right-aligned
    if (isNumericColumn(columnName, value)) {
      return 'text-right';
    }
    
    // Default to left alignment
    return 'text-left';
  };

  // Handle Excel export
  const handleExportExcel = async () => {
    setExporting(prev => ({ ...prev, excel: true }));
    try {
      const response = await api.post(
        '/analytics/export/excel',
        { data, columns },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `table-export-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExporting(prev => ({ ...prev, excel: false }));
    }
  };

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p className="text-gray-500">Invalid data format</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in-up">
      <div className="flex items-center justify-between animate-fade-in-down">
        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Table2 className="w-6 h-6 animate-float" style={{ color: COLORS.success }} />
          Results
        </h3>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleExportExcel}
            disabled={exporting.excel}
            className="px-4 py-2 text-white rounded-lg text-sm font-medium flex items-center gap-2 btn-animate hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed" 
            style={{ backgroundColor: COLORS.success }}
            title="Export as Excel"
          >
            {exporting.excel ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            {exporting.excel ? 'Exporting...' : 'Export Excel'}
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg overflow-hidden border border-gray-200 card-hover">
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead style={{ backgroundColor: '#F5F5F5' }} className="border-b border-gray-300">
              <tr>
                {columns.map((col) => {
                  const sampleValue = data[0]?.[col];
                  const alignment = getColumnAlignment(col, sampleValue);
                  const isIdColumn = col.toLowerCase() === 'id';
                  
                  return (
                    <th 
                      key={col} 
                      className={`px-6 py-4 text-xs font-bold text-gray-700 uppercase whitespace-nowrap ${alignment}`}
                    >
                      {col}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50 transition-colors duration-150">
                  {columns.map((col) => {
                    const value = row[col];
                    const isNumeric = isNumericColumn(col, value);
                    const isUrl = isUrlValue(value, col);
                    const isIdColumn = col.toLowerCase() === 'id';
                    const alignment = getColumnAlignment(col, value);
                    
                    // For ID column, display rowIndex + 1 instead of actual value
                    const displayValue = isIdColumn ? rowIndex + 1 : value;
                    
                    return (
                      <td 
                        key={col} 
                        className={`px-6 py-5 text-sm ${alignment} ${
                          isNumeric || isIdColumn ? 'font-bold text-gray-900' : 'text-gray-700'
                        } ${isUrl ? 'min-w-[200px]' : ''}`}
                      >
                        {isIdColumn ? displayValue : formatCellValue(value, col)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
          Showing {data.length} row{data.length !== 1 ? 's' : ''} with {columns.length} column{columns.length !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
};

export default ProductTable;

